// @flow
import React from 'react';
import './ParticipantCard.scss';
import '../../directory/Modal.scss';
import personIcon from '../../assets/head-icon.svg';
import chevronDown from '../../assets/chevron-down.svg';
import type { Participant } from '../../slices/participantSlice';
import {
  Menu,
  Dropdown,
  Button,
  Input,
  Tooltip,
  Select,
  Modal,
  AutoComplete,
  Checkbox,
  Divider,
  message,
} from 'antd';
import axios from 'axios';
import EllipsisOutlined from '@ant-design/icons/lib/icons/EllipsisOutlined';
import FileTextOutlined from '@ant-design/icons/lib/icons/FileTextOutlined';
import CheckCircleOutlined from '@ant-design/icons/lib/icons/CheckCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/lib/icons/CloseCircleOutlined';
import { useDispatch, useSelector } from 'react-redux';
import CloseSquareOutlined from '@ant-design/icons/lib/icons/CloseSquareOutlined';
import {
  deleteParticipant,
  patchParticipant,
  deleteParticipantForAllSchedules,
} from '../../slices/participantSlice';
import { showSkippedToast } from '../../utilities/scheduleTypeToast';
import type { State } from '../../general/types';
import type { ScheduleCoach } from '../../slices/coachesSlice';
import WarningOutlined from '@ant-design/icons/lib/icons/WarningOutlined';
import StopOutlined from '@ant-design/icons/lib/icons/StopOutlined';
import EnvironmentOutlined from '@ant-design/icons/lib/icons/EnvironmentOutlined';
import ClockCircleOutlined from '@ant-design/icons/lib/icons/ClockCircleOutlined';
import { ParticipantStatusTypesEnum } from '../../utilities/participantStatusTypesEnum';
const { TextArea } = Input;
const { Option } = Select;

export default function ParticipantCard({
  participantId,
  scheduleCoach,
  includeMenu,
  isMaster,
}: {
  participantId: number,
  scheduleCoach?: ScheduleCoach,
  includeMenu?: boolean,
  isMaster?: boolean,
}) {
  const dispatch = useDispatch();
  const [showNote, setShowNote] = React.useState(false);
  const [visible, setVisible] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);
  const [note, setNote] = React.useState(null);
  const [currentStatus, setCurrentStatus] = React.useState(null);
  const [selectedScheduleTypes, setSelectedScheduleTypes] = React.useState([]);
  const [applying, setApplying] = React.useState(false);

  const participant: Participant = useSelector((state: State) => {
    return state.participants.byId[participantId];
  });

  const schedule = useSelector((state: State) => {
    return state.schedule;
  });

  const skills = useSelector((state: State) => {
    return state.skills.list;
  });

  const removeParticipant = () => {
    if (scheduleCoach) {
      dispatch(
        deleteParticipant(
          participantId,
          scheduleCoach.id,
          currentStatus,
          isMaster
        )
      );
    }
  };

  const applyToSelectedScheduleTypes = async () => {
    if (!scheduleCoach || applying) return;
    setApplying(true);
    setShowModal(false);

    if (
      selectedScheduleTypes.length === 1 &&
      selectedScheduleTypes[0] === schedule.type.id
    ) {
      // Only current schedule type selected — use single update path
      removeParticipant();
      setApplying(false);
      return;
    }

    try {
      const result = await dispatch(
        deleteParticipantForAllSchedules(
          participantId,
          scheduleCoach.id,
          currentStatus,
          schedule.date,
          isMaster,
          selectedScheduleTypes
        )
      );
      showSkippedToast({
        action: 'updated',
        result,
        scheduleTypes: schedule.types,
      });
    } catch (err) {
      message.error('Failed to update participant');
    } finally {
      setApplying(false);
    }
  };

  const saveNote = () => {
    dispatch(patchParticipant({ id: participant.id, note: note }));
  };

  const checkIn = () => {
    dispatch(
      patchParticipant({
        id: participant.id,
        requiresMoDropOff: !participant.requiresMoDropOff,
      })
    );
  };

  const [showAddressModal, setShowAddressModal] = React.useState(false);
  const [selectedSavedAddress, setSelectedSavedAddress] = React.useState(
    undefined
  );
  const [adhocAddressInput, setAdhocAddressInput] = React.useState('');
  const [adhocOptions, setAdhocOptions] = React.useState([]);
  const [selectedPlace, setSelectedPlace] = React.useState(null);
  const placeIdMapRef = React.useRef({});
  const searchTimeoutRef = React.useRef(null);

  const justSelectedRef = React.useRef(false);

  const openAddressModal = () => {
    // Pre-select current state on open
    if (participant.adhocAddress) {
      setSelectedSavedAddress(undefined);
      setAdhocAddressInput(participant.adhocAddress);
      setSelectedPlace(participant.adhocAddress);
    } else if (participant.addressOverride) {
      setSelectedSavedAddress(participant.addressOverride.id);
      setAdhocAddressInput('');
      setSelectedPlace(null);
    } else {
      setSelectedSavedAddress('default');
      setAdhocAddressInput('');
      setSelectedPlace(null);
    }
    setAdhocOptions([]);
    setShowAddressModal(true);
  };

  const saveAddress = async () => {
    try {
      if (selectedPlace) {
        // One-time address
        const googleUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
          selectedPlace
        )}`;
        await dispatch(
          patchParticipant({
            id: participant.id,
            adhocAddress: selectedPlace,
            adhocAddressGoogleUrl: googleUrl,
            addressOverrideId: null,
          })
        );
      } else if (selectedSavedAddress === 'default') {
        // Default — clear both overrides
        await dispatch(
          patchParticipant({
            id: participant.id,
            addressOverrideId: null,
            adhocAddress: null,
            adhocAddressGoogleUrl: null,
          })
        );
      } else if (selectedSavedAddress) {
        // Saved address
        await dispatch(
          patchParticipant({
            id: participant.id,
            addressOverrideId: selectedSavedAddress,
            adhocAddress: null,
            adhocAddressGoogleUrl: null,
          })
        );
      }
      message.success('Address updated successfully');
    } catch (error) {
      message.error('Failed to update address');
    }
    setShowAddressModal(false);
    setAdhocAddressInput('');
    setSelectedPlace(null);
    setAdhocOptions([]);
    setSelectedSavedAddress(undefined);
  };

  const searchPlaces = (value) => {
    setAdhocAddressInput(value);
    setSelectedSavedAddress(undefined);

    // Don't trigger a new search when onSearch fires after a selection
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    setSelectedPlace(null);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value || value.length < 3) {
      setAdhocOptions([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await axios.get(
          `${
            process.env.REACT_APP_SERVER_URL || ''
          }/schedules/google_places_autocomplete/`,
          { params: { input: value } }
        );
        const predictions = response.data.predictions || [];
        const idMap = {};
        predictions.forEach((p) => {
          idMap[p.description] = p.place_id;
        });
        placeIdMapRef.current = idMap;
        setAdhocOptions(
          predictions.map((p) => ({
            value: p.description,
            label: p.description,
          }))
        );
      } catch (err) {
        setAdhocOptions([]);
      }
    }, 300);
  };

  const onSelectPlace = (value) => {
    justSelectedRef.current = true;
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setAdhocAddressInput(value);
    setSelectedPlace(value);
  };

  const formatAddressTooltip = (effectiveAddress) => {
    if (!effectiveAddress) return 'No address';

    if (effectiveAddress.isAdhoc) {
      return `One-Time: ${effectiveAddress.address}`;
    }

    const parts = [
      effectiveAddress.address,
      effectiveAddress.city,
      effectiveAddress.state,
    ].filter(Boolean);
    const formatted =
      parts.join(', ') +
      (effectiveAddress.zip ? ` ${effectiveAddress.zip}` : '');
    const label = effectiveAddress.label ? `${effectiveAddress.label} - ` : '';
    return label + formatted;
  };

  if (!participant) {
    return <div></div>;
  }

  if (typeof note !== 'string') {
    setNote(participant & participant.note || '');
  }

  const onChange = (value) => {
    dispatch(
      patchParticipant({
        id: participant.id,
        skill_id: value,
      })
    );
  };

  const menu = (
    <div className="user-menu">
      {showNote ? (
        <div
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          className="note-menu"
        >
          <TextArea
            autoFocus
            rows={4}
            placeholder="Type your note here..."
            onChange={(e) => setNote(e.target.value)}
            value={note}
          />
          <Button
            type="link"
            className="close"
            onClick={() => setVisible(false)}
          >
            <CloseSquareOutlined />
          </Button>
          <Button
            className="submit"
            onClick={() => {
              setVisible(false);
              saveNote();
            }}
          >
            Submit
          </Button>
        </div>
      ) : (
        <Menu selectable={false}>
          <Menu.Item
            onClick={() => {
              setNote(participant.note);
              setShowNote(true);
            }}
          >
            <FileTextOutlined /> Add Note
          </Menu.Item>
          <Menu.Item
            onClick={() => {
              setVisible(false);
              checkIn();
            }}
            className={participant.requiresMoDropOff && 'remove'}
          >
            <CheckCircleOutlined />{' '}
            {participant.requiresMoDropOff ? 'Remove' : 'Add'} Check-In
          </Menu.Item>

          <Menu.Item
            onClick={() => {
              setVisible(false);
              setSelectedScheduleTypes([schedule.type.id]);
              setShowModal(true);
              setCurrentStatus(ParticipantStatusTypesEnum.AVAILABLE);
            }}
          >
            <CloseCircleOutlined /> Remove Participant
          </Menu.Item>

          <Menu.Item
            key="change-address"
            onClick={() => {
              setVisible(false);
              openAddressModal();
            }}
          >
            <EnvironmentOutlined /> Change Address
          </Menu.Item>

          {participant.status === ParticipantStatusTypesEnum.CALLED_OUT ? (
            <Menu.Item
              className="status-selected"
              onClick={() => {
                setVisible(false);
                setSelectedScheduleTypes([schedule.type.id]);
                setShowModal(true);
                setCurrentStatus(ParticipantStatusTypesEnum.SCHEDULED);
              }}
            >
              <WarningOutlined /> Called Out
            </Menu.Item>
          ) : (
            <Menu.Item
              onClick={() => {
                setVisible(false);
                setSelectedScheduleTypes([schedule.type.id]);
                setShowModal(true);
                setCurrentStatus(ParticipantStatusTypesEnum.CALLED_OUT);
              }}
            >
              <WarningOutlined /> Called Out
            </Menu.Item>
          )}

          {participant.status === ParticipantStatusTypesEnum.NO_SHOW ? (
            <Menu.Item
              className="status-selected"
              onClick={() => {
                setVisible(false);
                setSelectedScheduleTypes([schedule.type.id]);
                setShowModal(true);
                setCurrentStatus(ParticipantStatusTypesEnum.SCHEDULED);
              }}
            >
              <StopOutlined /> No Show
            </Menu.Item>
          ) : (
            <Menu.Item
              onClick={() => {
                setVisible(false);
                setSelectedScheduleTypes([schedule.type.id]);
                setShowModal(true);
                setCurrentStatus(ParticipantStatusTypesEnum.NO_SHOW);
              }}
            >
              <StopOutlined /> No Show
            </Menu.Item>
          )}
        </Menu>
      )}
    </div>
  );

  const renderSkills = () => {
    return skills.map((skill) => {
      return (
        <Option
          key={`${skill.id}_${participant.id}`}
          value={skill.id}
          label={skill.name}
        >
          {skill.name}
        </Option>
      );
    });
  };

  return (
    <div className="participant-card on-hover">
      <div>
        {<img src={personIcon} alt="head" width="25" height="25"></img>}{' '}
        <Tooltip title={formatAddressTooltip(participant.effectiveAddress)}>
          <label className="name" htmlFor={participantId}>
            {participant.lastName}, {participant.firstName}
          </label>
        </Tooltip>
        <Select
          id={participantId}
          disabled={!includeMenu}
          showSearch
          style={{
            width:
              participant.note ||
              participant.requiresMoDropOff ||
              participant.addressOverride ||
              participant.adhocAddress
                ? 170
                : 194.8,
          }}
          placeholder="Select Skill"
          optionFilterProp="children"
          onChange={onChange}
          defaultValue={
            participant.skill?.id ? participant.skill.id : undefined
          }
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          suffixIcon={
            <img src={chevronDown} alt="chevron" width="30" height="30" />
          }
        >
          {renderSkills()}
        </Select>
      </div>
      {
        <div className="rightMenu">
          {participant.status === ParticipantStatusTypesEnum.OFF && (
            <WarningOutlined />
          )}
        </div>
      }
      {includeMenu && (
        <div className="right-menu">
          <Dropdown
            overlay={menu}
            trigger={['click']}
            visible={visible}
            onVisibleChange={setVisible}
            className={`ant-dropdown-link hover ${visible ? 'visible' : ''}`}
            onClick={(e) => {
              setShowNote(false);
              e.preventDefault();
            }}
          >
            <EllipsisOutlined />
          </Dropdown>
          <Modal
            width="500px"
            visible={showModal}
            onCancel={() => setShowModal(false)}
            title="Apply to which schedule types?"
            footer={[
              <Button key="cancel" onClick={() => setShowModal(false)}>
                Cancel
              </Button>,
              <Button
                key="apply"
                type="primary"
                loading={applying}
                disabled={applying || selectedScheduleTypes.length === 0}
                onClick={applyToSelectedScheduleTypes}
              >
                Apply
              </Button>,
            ]}
          >
            <Checkbox.Group
              value={selectedScheduleTypes}
              onChange={(checkedValues) =>
                setSelectedScheduleTypes(checkedValues)
              }
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              {schedule.types.map((type) => (
                <Checkbox
                  key={type.id}
                  value={type.id}
                  style={{
                    marginLeft: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontSize: '14px' }}>{type.type}</span>
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Modal>
          {(participant.note ||
            participant.requiresMoDropOff ||
            participant.addressOverride ||
            participant.adhocAddress) && (
            <div className="status-box">
              {participant.note && (
                <div>
                  <Tooltip title={participant.note}>
                    <span>
                      <FileTextOutlined style={{ color: 'white' }} />
                    </span>
                  </Tooltip>
                </div>
              )}
              {participant.requiresMoDropOff && (
                <div>
                  <CheckCircleOutlined style={{ color: 'white' }} />
                </div>
              )}
              {participant.addressOverride && (
                <div>
                  <Tooltip
                    title={`Address: ${
                      participant.addressOverride.label ||
                      participant.addressOverride.address
                    }`}
                  >
                    <span>
                      <EnvironmentOutlined style={{ color: 'white' }} />
                    </span>
                  </Tooltip>
                </div>
              )}
              {participant.adhocAddress && (
                <div>
                  <Tooltip
                    title={`One-Time Address: ${participant.adhocAddress}`}
                  >
                    <span>
                      <ClockCircleOutlined style={{ color: 'white' }} />
                    </span>
                  </Tooltip>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <Modal
        title="Change Address"
        visible={showAddressModal}
        onCancel={() => {
          setShowAddressModal(false);
          setAdhocAddressInput('');
          setSelectedPlace(null);
          setAdhocOptions([]);
          setSelectedSavedAddress(undefined);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowAddressModal(false);
              setAdhocAddressInput('');
              setSelectedPlace(null);
              setAdhocOptions([]);
              setSelectedSavedAddress(undefined);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="set"
            type="primary"
            disabled={!selectedPlace && !selectedSavedAddress}
            onClick={saveAddress}
          >
            Set Address
          </Button>,
        ]}
      >
        <div>
          <label>Saved Addresses:</label>
          <Select
            style={{ width: '100%' }}
            value={selectedSavedAddress}
            onChange={(value) => {
              setSelectedSavedAddress(value);
              setSelectedPlace(null);
              setAdhocAddressInput('');
              setAdhocOptions([]);
            }}
            placeholder="Select an address"
          >
            <Option value="default">Default</Option>
            {participant.addresses &&
              participant.addresses.map((addr) => (
                <Option key={addr.id} value={addr.id}>
                  {addr.label
                    ? `${addr.label} - ${addr.address}`
                    : addr.address}
                </Option>
              ))}
          </Select>
        </div>
        {!isMaster && (
          <>
            <Divider>OR</Divider>
            <div>
              <label>One-Time Address:</label>
              <AutoComplete
                style={{ width: '100%' }}
                options={adhocOptions}
                onSearch={searchPlaces}
                onSelect={onSelectPlace}
                value={adhocAddressInput}
                placeholder="Start typing to search..."
              />
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
